// /frontend/src/pages/Applicants.js

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { 
    Box, Typography, CircularProgress, Alert, Paper, List, ListItem, 
    ListItemText, Divider, Button, Chip, Modal, Avatar, Link as MuiLink 
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 600,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto'
};

const Applicants = () => {
  const { jobId } = useParams();
  const [applicants, setApplicants] = useState([]);
  const [jobTitle, setJobTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [selectedApplicant, setSelectedApplicant] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [jobRes, applicantsRes] = await Promise.all([
          axios.get(`/api/jobs/${jobId}`),
          axios.get(`/api/jobs/${jobId}/applications`)
        ]);
        setJobTitle(jobRes.data.title);
        setApplicants(applicantsRes.data);
      } catch (err) {
        setError('Impossibile caricare i dati dei candidati.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [jobId]);

  const handleOpenModal = (applicantUser) => setSelectedApplicant(applicantUser);
  const handleCloseModal = () => setSelectedApplicant(null);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Candidati per: "{jobTitle}"</Typography>
      <Paper>
        <List>
          {applicants.length > 0 ? applicants.map((app, index) => (
            <React.Fragment key={app.id}>
              <ListItem 
                alignItems="flex-start"
                secondaryAction={
                  <Button variant="outlined" size="small" onClick={() => handleOpenModal(app.user)}>
                    Vedi Profilo Completo
                  </Button>
                }
              >
                <ListItemText
                  primary={
                    // --- MODIFICA QUI ---
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="h6">{app.user.fullName}</Typography>
                      <Chip 
                        label={app.user.availabilityStatus === 'attivo' ? 'Disponibile' : 'Non Disponibile'}
                        color={app.user.availabilityStatus === 'attivo' ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography component="p" variant="body2" color="text.primary" sx={{ mt: 1 }}>
                        <strong>📍 Indirizzo:</strong> {app.user.address || 'Non specificato'}
                      </Typography>
                      <Typography component="p" variant="body2" color="text.primary">
                        <strong>🎓 Studi:</strong> {app.user.education || 'Non specificato'}
                      </Typography>
                      <Typography component="p" variant="body2" color="text.primary">
                        <strong>🛠️ Competenze:</strong> {app.user.categories.map(c => c.category.name).join(', ') || 'Non specificate'}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
              {index < applicants.length - 1 && <Divider component="li" />}
            </React.Fragment>
          )) : <Typography sx={{ p: 2 }}>Nessun candidato per questa posizione al momento.</Typography>}
        </List>
      </Paper>

      <Modal open={!!selectedApplicant} onClose={handleCloseModal}>
        <Box sx={modalStyle}>
          {selectedApplicant && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ width: 56, height: 56, mr: 2, bgcolor: 'primary.main' }}>
                  {selectedApplicant.fullName.charAt(0)}
                </Avatar>
                <Typography variant="h5">{selectedApplicant.fullName}</Typography>
              </Box>
              
              <Divider textAlign="left" sx={{ mb: 2 }}>CONTATTI</Divider>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EmailIcon color="action" sx={{ mr: 1 }} />
                <Typography variant="body1">{selectedApplicant.email}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PhoneIcon color="action" sx={{ mr: 1 }} />
                <Typography variant="body1">{selectedApplicant.phone}</Typography>
              </Box>
              
              <Divider textAlign="left" sx={{ my: 2 }}>INFORMAZIONI PERSONALI</Divider>
              <Typography gutterBottom><strong>Indirizzo:</strong> {selectedApplicant.address}</Typography>
              <Typography gutterBottom><strong>Nato/a il:</strong> {new Date(selectedApplicant.birthDate).toLocaleDateString('it-IT')}</Typography>
              <Typography gutterBottom><strong>Luogo di Nascita:</strong> {selectedApplicant.birthPlace}</Typography>

              <Divider textAlign="left" sx={{ my: 2 }}>PROFILO PROFESSIONALE</Divider>
              <Typography gutterBottom><strong>Studi:</strong> {selectedApplicant.education}</Typography>
              <Typography gutterBottom>
                <strong>LinkedIn:</strong> {selectedApplicant.linkedin ? <MuiLink href={selectedApplicant.linkedin} target="_blank" rel="noopener noreferrer">Apri profilo</MuiLink> : 'Non fornito'}
              </Typography>
              <Typography gutterBottom>
                <strong>Curriculum Vitae:</strong> 
                {selectedApplicant.resume ? <MuiLink href={`/${selectedApplicant.resume}`} target="_blank">Scarica CV</MuiLink> : 'Non caricato'}
              </Typography>
              <Typography gutterBottom sx={{ mt: 1 }}><strong>Competenze:</strong></Typography>
              <Box>
                {selectedApplicant.categories.map(c => <Chip key={c.categoryId} label={c.category.name} sx={{ mr: 1, mb: 1 }} />)}
              </Box>

              <Button variant="contained" onClick={handleCloseModal} sx={{ mt: 3, float: 'right' }}>
                Chiudi
              </Button>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default Applicants;